import React, {useMemo} from 'react'
import {Col, Form} from 'react-bootstrap'
import useWidget from '../../../api/hooks/useWidget'
import ComponentDashboard from '../../components/ComponentDashboard'
import ComponentFilters from '../../components/ComponentFilters'
import {KTSVG} from '../../../../_metronic/helpers'
import ProgressBarModule from './src/ProgressBar'
import {extractImagePaths, getResolvedLink, useFilter} from '../helpers'
import {useIntl} from 'react-intl'
import ComponentTooltip from '../../components/ComponentTooltip'
import useUpdate from '../../../api/hooks/useUpdate'
import ComponentButton from '../../components/ComponentButton'
import moment from 'moment'
import api from '../../../api'
import {useRefetchSubscribers, useSubscribeOnRefetch} from '../helpers/PageContext'
import {
    TModuleWidgets,
    TModuleWidgetsHardReport,
    TModuleWidgetsSimpleReport,
    TModuleWidgetsToolbar,
    TModuleWidgetsWidget,
    TModuleWidgetsWidgetGraphModule,
} from './_types'
import Skeleton from 'react-loading-skeleton'
import SkeletonWidget from './src/SkeletonWidget'
import GraphModulePreloader from './src/GraphModulePreloader'
import valuesCleaner from '../../helpers/valuesCleaner'
import {useThemeVersion} from '../../helpers/ThemeContext'
import DashboardPreview from './src/DashboardPreview'
import {useSetupContext} from '../../helpers/SetupContext'


const CharModule = React.lazy(() => import("./src/Char"));
const CharModuleApex = React.lazy(() => import("./src/newUI/CharModuleApex"));

const WidgetGraphModule: React.FC<TModuleWidgetsWidgetGraphModule> = ({ detail }) => {
    const intl = useIntl()
    const {version} = useThemeVersion()
    const { type, settings } = detail
    switch (type) {
        case "progress_bar":
            return <ProgressBarModule title={settings.title} percent={settings.percent} />
        case "char":
        case "details_char":
          if (version === 'v7') {
            return <CharModule char={settings.char} withDetails={type === "details_char"} value_title={settings.value_title} />
          } else {
            return (
              <CharModuleApex
                char={settings.char}
                title={settings.value_title}
                chartType={settings.chartType}
                colors={settings.colors}
              />
            )
          }
        default:
            return <div className="card-body">
                <div className='d-flex flex-column w-100'>
                    <span className='text-dark me-2 fw-bold pb-3'>{`${intl.formatMessage({ id: "WIDGET.UNDEFINDED_MODULE" })} ${type}`}</span>
                </div>
            </div>
    }
}

const Widget: React.FC<TModuleWidgetsWidget> = ({ data, isRefetching }) => {
    const { prefix, value, postfix, description, detail, size = 2 } = data
    const havePostfix = !Array.isArray(postfix) && postfix.value

    const showGraphModule = useMemo(() => {
        if (Array.isArray(detail)) {
            return false
        } else {
            switch (detail.type) {
                case "char":
                case "details_char":
                    return Boolean(detail.settings?.char?.x?.length) ?? false
                default:
                    return true
            }
        }
    }, [detail])

    const widgetSize = size * 3
    return <Form.Group className="moduleWidgets_widgetContainer active" as={Col} md={widgetSize}>

        <div className="moduleWidgets_widget card card-flush">
            <div className="moduleWidgets_widgetContent card-header">
                <div className='card-title flex-column mw-100'>
                    <div className="moduleWidgets_widgetValueContainer">
                        <span className="moduleWidgets_widgetValuePrefix">{prefix}</span>
                        <ComponentTooltip title={value}>
                            <span className="moduleWidgets_widgetValue">{isRefetching ? <Skeleton className="skeleton" width={120} /> : value}</span>
                        </ComponentTooltip>
                        {
                            havePostfix ? <span className={`badge badge-light-${postfix?.background ?? "success"} fs-base`}>
                                {postfix?.icon ? <KTSVG
                                    path='/media/icons/duotune/arrows/arr066.svg'
                                    className='svg-icon-5 svg-icon-success ms-n1'
                                /> : null}
                                {' '}
                                {postfix?.value}
                            </span> : null
                        }
                    </div>
                    <span className='moduleWidgets_widgetDescription'>{isRefetching ? <Skeleton className="skeleton" width={200} /> : description}</span>
                </div>
            </div>
            {showGraphModule ?
                <React.Suspense fallback={<GraphModulePreloader />}>
                    <WidgetGraphModule detail={detail as TModuleWidgetsWidgetGraphModule["detail"]} />
                </React.Suspense>
                : null}
        </div>

    </Form.Group>
}
const ModuleWidgetsSimpleReport: React.FC<TModuleWidgetsSimpleReport> = ({ data, isRefetching }) => {
    return <>
        {data.map(widget => <Widget key={widget.description + widget.value} data={widget} isRefetching={isRefetching} />)}
    </>
}
const ModuleWidgetsHardReport: React.FC<TModuleWidgetsHardReport> = ({ data, isRefetching }) => {
    const { report, status } = data
    return <div className="moduleWidgets_hardReportContainer">
        {status === "no_cache" ? <div className="moduleWidgets_infoCard">
            <span>По данному отчёту еще не запрашивалась информация. Пожалуйста, нажмите на кнопку "Обновить отчёт".</span>
        </div> :
            status === "updating" ? <div className="moduleWidgets_infoCard">
                <span>Отчёт в процессе формирования. Пожалуйста, ожидайте.</span>
            </div> :
                report?.map(widget => <Widget key={widget.description + widget.value} data={widget} isRefetching={isRefetching} />)}
    </div>
}
const ModuleWidgetsToolbar: React.FC<TModuleWidgetsToolbar> = props => {
    const { updated_at, status, handleUpdateHardReport } = props
    const resolvedUpdateTime = updated_at ? moment(updated_at).format("DD.MM.YY г. HH:mm") : "-"
    const isButtonDisabled = status === "updating"
    return <div className="moduleWidgets_toolbar">
        <div className="moduleWidgets_updateTime">{`Последнее обновление: ${resolvedUpdateTime}`}</div>
        <ComponentButton type="custom" settings={{ title: "Обновить отчёт", background: "dark", icon: "" }} customHandler={handleUpdateHardReport} disabled={isButtonDisabled} />
    </div>
}

export const ModuleWidgets = React.memo<TModuleWidgets>(props => {
    const { components, settings, hook } = props
    const { widgets_group: widgetCommand, filters: initialFilters, linked_filter } = settings
    const { filter, isInitials, setFilter, resetFilter } = useFilter(`${props.type}_${widgetCommand}`, initialFilters, linked_filter)
    const haveFilter = Boolean(components?.filters)
    const haveButtons = Boolean(components?.buttons)
    const { data, isLoading, isRefetching, refetch } = useWidget(widgetCommand, valuesCleaner(filter))

    useSubscribeOnRefetch(refetch, setFilter, linked_filter)

    useRefetchSubscribers(`${props.type}_${widgetCommand}`, isRefetching, Boolean(linked_filter), filter)


    useUpdate({functions: [{ active: Boolean(hook), update: refetch }], table_name: hook, refetchInterval: 2000})

    const showToolbar = data && !Array.isArray(data)
    const handleUpdateHardReport = async () => {
        await api("hardReports", "add", { reportArticle: widgetCommand, filters: filter })
        setTimeout(refetch, 1000)
    }

    type TWidgetPreview = {
        value: string
        description: string
        dashboardImage?: string | string[]
    }

    const arrayData = Array.isArray(data) ? (data as TWidgetPreview[]) : []
    const first = arrayData[0]

    const heroTitle    = first?.value
    const heroSubtitle = first?.description

    const rawPaths = extractImagePaths(first?.dashboardImage)
    const urls = rawPaths.length
        ? rawPaths.map(p => getResolvedLink(p)!).filter(Boolean)
        : []

    const heroImage = urls[0]

    if (heroImage) {
        return (
            <div className="moduleWidgets">
                <DashboardPreview title={heroTitle} subtitle={heroSubtitle} image={heroImage} />
            </div>
        )
    }

    return <div className="moduleWidgets">
        {
            haveButtons || haveFilter ?
                <ComponentDashboard>
                    {haveButtons ? components.buttons.map(button => <ComponentButton key={button.settings.title} className="dark-light" {...button} />) : null}
                    {haveFilter ? <ComponentFilters
                        type="dropdown"
                        data={components.filters}
                        filterValues={filter}
                        isInitials={isInitials}
                        fullWidth={false}
                        handleChange={setFilter}
                        handleReset={resetFilter}
                    /> : null}
                </ComponentDashboard> : null
        }

        {showToolbar ? <ModuleWidgetsToolbar updated_at={data.updated_at} status={data.status} handleUpdateHardReport={handleUpdateHardReport} /> : null}
        {isLoading ? <SkeletonWidget /> : null}
        {data ? Array.isArray(data) ? <ModuleWidgetsSimpleReport data={data} isRefetching={isRefetching} /> :
            <ModuleWidgetsHardReport data={data} isRefetching={isRefetching} /> : null}
    </div>
})